import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "../../../components/RestaurantAdminComponents/DataTable";
import FilterToolbar from "../../../components/Common/FilterToolbar";
import SecondaryButton from "../../../components/Common/SecondaryButton";
import { Container } from "reactstrap";
import ActionMenu from "../../../components/Common/ActionMenu";
import { get, del } from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import DeleteConfirmationModal from "../../../components/Common/DeleteConfirmationModal";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import { extractErrorMessage } from "../../../utils/errorHandler";
import tickIcon from "../../../assets/images/support/tick-circle-white.svg";

/* -------------------------------------------------------------------------
 * AdminGamesPage — full "Games" table view. No sidebar or
 * header here; this is content-only, dropped into whatever shell layout
 * the app already provides.
 * ---------------------------------------------------------------------- */

const AdminGamesPage = ({ onFilterClick, onExportClick }) => {
  document.title = "Games | Omaha Food Lovers";
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [gameToDelete, setGameToDelete] = useState(null);

  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  const columns = [
    { key: "name", label: "Name", width: "1.5fr" },
    { key: "slug", label: "Slug", width: "1.2fr" },
    { key: "type", label: "Type", width: "0.8fr" },
    { key: "rewardPoints", label: "Reward Points", width: "0.8fr" },
    { key: "targetDetails", label: "Target Details", width: "1.5fr" },
    { key: "restaurantScope", label: "Restaurant Scope", width: "1fr" },
    { key: "isActive", label: "Status", width: "0.8fr" },
    { key: "createdAt", label: "Created At", width: "1fr" },
    {
      key: "actions",
      label: "",
      width: "60px",
      align: "right",
      className: "rp-table__cell--actions",
      render: (row) => (
        <ActionMenu
          isOpen={activeDropdown === row.id}
          onToggle={() => setActiveDropdown(activeDropdown === row.id ? null : row.id)}
          items={[
            {
              label: "Edit",
              icon: "edit",
              onClick: () => navigate(`/admin/games/${row.slug}/edit`, { state: { gameData: row } }),
            },
            {
              label: "Delete",
              icon: "delete",
              onClick: () => handleDelete(row.slug),
              danger: true,
            },
          ]}
        />
      ),
    },
  ];

  const buildQueryParams = (pageNum = 1) => {
    const params = new URLSearchParams();
    params.append("page", pageNum);
    params.append("per_page", 15);
    params.append("sort_by", sortBy);
    params.append("sort_order", sortOrder);

    if (search.trim()) {
      params.append("search", search.trim());
    }

    return params.toString();
  };

  const transformGameData = (gamesArray) =>
    gamesArray.map((game) => {
      const config = game.config || {};
      let targetDetails = "N/A";

      if (game.type === "streak") {
        targetDetails = `${config.target_duration_weeks || 0} weeks`;
      } else if (game.type === "visits") {
        targetDetails = `${config.target_visits_required || 0} visits in ${config.target_timeframe_weeks || 0} weeks`;
      }

      const restaurantScope = config.eligible_restaurants?.scope === "selected"
        ? `${config.eligible_restaurants?.restaurant_ids?.length || 0} selected`
        : "All";

      return {
        id: game.id,
        slug: game.slug,
        name: game.name || "Unknown",
        type: game.type || "Unknown",
        description: game.description || "N/A",
        rewardPoints: config.reward_points || 0,
        targetDetails,
        restaurantScope,
        isActive: game.is_active ? "Active" : "Inactive",
        createdAt: game.created_at ? new Date(game.created_at).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }) : "N/A",
        config: config,
      };
    });

  const fetchGames = async (pageNum = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await get(`${endpoints.adminGames}?${buildQueryParams(pageNum)}`);

      if (response.data?.data) {
        const newGames = response.data.data || [];
        const transformedGames = transformGameData(newGames);

        if (isLoadMore) {
          setGames((prev) => [...prev, ...transformedGames]);
        } else {
          setGames(transformedGames);
        }

        setHasMore(newGames.length === 15);
        setPage(pageNum);
      }
    } catch (error) {
      console.error("Failed to fetch games:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchGames(page + 1, true);
    }
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
    setHasMore(true);
  };

  const handleDelete = (gameSlug) => {
    setGameToDelete(gameSlug);
    setShowDeleteModal(true);
    setActiveDropdown(null);
  };

  const confirmDelete = async () => {
    if (!gameToDelete) return;

    try {
      const deleteEndpoint = endpoints.adminDeleteGame.replace('{{game_slug}}', gameToDelete);
      await del(deleteEndpoint);
      toastr.success("Game deleted successfully");
      setShowDeleteModal(false);
      setGameToDelete(null);
      fetchGames(1, false);
    } catch (error) {
      console.error("Failed to delete game:", error);
      toastr.error("Failed to delete game");
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setGameToDelete(null);
  };

  const handleExport = async () => {
    try {
      const response = await get(`${endpoints.adminExport}?type=mini_games`);
      
      // Create a blob from the response data
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'mini_games.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toastr.success("Export successful!");
    } catch (error) {
      console.error("Failed to export data:", error);
      const errorMessage = extractErrorMessage(error);
      toastr.error(errorMessage);
    }
  };

  // Filter configuration for FilterToolbar
  const filters = [
    {
      key: "sortBy",
      type: "sortBy",
      label: "Sort By",
      value: sortBy,
      onChange: setSortBy,
      defaultValue: "created_at",
      options: [
        { value: "name", label: "Name" },
        { value: "slug", label: "Slug" },
        { value: "type", label: "Type" },
        { value: "is_active", label: "Status" },
        { value: "created_at", label: "Created At" },
      ],
    },
    {
      key: "sortOrder",
      type: "sortDirection",
      label: "Sort Order",
      value: sortOrder,
      onChange: setSortOrder,
      defaultValue: "desc",
    },
  ];

  useEffect(() => {
    fetchGames(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy, sortOrder]);

  return (
    <div className="page-content rp-page-content">
      <Container fluid>
        <div className="rp-page">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: "12px" }}>
            <div>
              <h2 className="rp-card__title" style={{ margin: 0 }}>Games</h2>
              <p className="rp-card__subtitle" style={{ margin: 0 }}>Overview of mini-games and engagement activities.</p>
            </div>
            <SecondaryButton
              variant="orange"
              icon={tickIcon}
              onClick={() => navigate("/admin/games/create")}
            >
              Add Game
            </SecondaryButton>
          </div>
          <FilterToolbar
            searchValue={search}
            onSearchChange={handleSearchChange}
            searchPlaceholder="Search by name or slug"
            filters={filters}
            showExport
            onExport={handleExport}
          />

          <DataTable
            columns={columns}
            rows={games}
            getRowKey={(r) => r.id}
            loading={loading}
            loadingMore={loadingMore}
            onLoadMore={handleLoadMore}
            hasMore={hasMore}
          />
        </div>
      </Container>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        title="Delete Game"
        message="Are you sure you want to delete this game? This action cannot be undone."
        onCancel={cancelDelete}
        onConfirm={confirmDelete}
        itemName="game"
      />
    </div>
  );
};

AdminGamesPage.propTypes = {
  onFilterClick: PropTypes.func,
  onExportClick: PropTypes.func,
};

AdminGamesPage.defaultProps = {
  onFilterClick: () => {},
  onExportClick: () => {},
};

export default AdminGamesPage;
